import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAsynApplicationStepData,
  getCurrentStepData,
} from "../../redux/transactions/UntappedSlice";
import { Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import axios from "axios";

const { Dragger } = Upload;

function PaymentInformation({ data,currentApplicant, onChangePaymentInfo }) {
  // Initialize state for each field
  const defaultFormData = {
    type: "", 
    phone: "",
    fileId: null,
  };

  const dispatch = useDispatch();
  const motarFileUrl = "https://applicationbackend.jalikoi.rw/api/v1";

  const [formData, setFormData] = useState({ ...defaultFormData });
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const phoneNumber = useSelector((state) => state.untapped.phoneNumber);
  const currentStepSavedData = useSelector(getCurrentStepData);


  // Fetch saved step data
  useEffect(() => {
    if (phoneNumber?.trim().length === 10) {
      dispatch(
        fetchAsynApplicationStepData({ phone: phoneNumber.trim(), step: 9 })
      );
    }
  }, [dispatch, phoneNumber]);

  // Sync formData with saved step data
  useEffect(() => {
    if (currentStepSavedData?.step_data) {
      const paymentInfo = currentStepSavedData.step_data.payment_info || {};

      if (paymentInfo !== null) {
        setFormData((prev) => ({
          ...prev,
          type: paymentInfo.type ?? "",
          phone: paymentInfo.phone ?? "",
          fileId: paymentInfo.fileId ?? null,
        }));
      }
    }
  }, [currentStepSavedData]);

  // Update state only if incoming `data` changes
  useEffect(() => {
    if (data && JSON.stringify(data) !== JSON.stringify(formData)) {
      setFormData({ ...defaultFormData, ...data });
      
      // If data contains a fileId, you might want to show it was previously uploaded
      if (data.fileId) {
        setFileList([
          {
            uid: "-1",
            name: "payment-receipt.pdf",
            status: "done",
          },
        ]);
      }
    }
  }, [data]);

  // Notify parent only if `formData` changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (JSON.stringify(formData) !== JSON.stringify(data)) {
        onChangePaymentInfo(formData);
      }
    }, 500); // Debounce updates

    return () => clearTimeout(timeout);
  }, [formData, data, onChangePaymentInfo]);

  // Handle payment type selection
  const handlePaymentTypeChange = (e) => {
    const selectedType = e.target.value;
    setFormData((prev) => ({
      ...prev,
      type: selectedType,
      // Clear the other field when switching types
      phone: selectedType === "pay_momo" ? prev.phone : "",
      fileId: selectedType === "pay_file" ? prev.fileId : null,
    }));

    // Clear file list if switching to momo
    if (selectedType === "pay_momo") {
      setFileList([]);
    }
  };

  // Handle phone input change
  const handlePhoneChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  // Upload file to server
  const uploadFileToServer = async (file) => {
    const uploadFormData = new FormData();
    uploadFormData.append("file_RECEIPT", file);

    try {
      setUploading(true);
      const uploadResponse = await axios.post(
        `${motarFileUrl}/upload/${currentApplicant}`,
        uploadFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

    

      // API returns an array with one object containing the file info
      const responseData = uploadResponse.data;
      
      if (!Array.isArray(responseData) || responseData.length === 0) {
        throw new Error("Invalid response format from server");
      }

      const fileId = responseData[0].id;
      
      if (!fileId) {
        throw new Error("File ID not returned from server");
      }

      message.success("Dosiye yinjijwe neza!");
      return fileId;
    } catch (error) {
      message.error("Ikibazo mu kohereza dosiye. Gerageza ukundi!");
      console.error("Upload error:", error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  // File upload configuration
  const uploadProps = {
    name: "file",
    multiple: false,
    fileList: fileList,
    accept: "image/*,.pdf",
    customRequest: async ({ file, onSuccess, onError }) => {
      // Validate file type
      const isValidType =
        file.type.startsWith("image/") || file.type === "application/pdf";
      if (!isValidType) {
        message.error("Winjiza gusa ifoto cyangwa PDF!");
        onError(new Error("Invalid file type"));
        return;
      }

      // Validate file size (max 5MB)
      const isValidSize = file.size / 1024 / 1024 < 5;
      if (!isValidSize) {
        message.error("Dosiye ntabwo ishobora kurenza 5MB!");
        onError(new Error("File too large"));
        return;
      }

      // Upload file immediately
      try {
        const fileId = await uploadFileToServer(file);
        
        // Update form data with file ID
        setFormData((prev) => ({
          ...prev,
          fileId: fileId,
        }));

        onSuccess({ id: fileId });
      } catch (error) {
        onError(error);
      }
    },
    onChange: (info) => {
      const { file, fileList: newFileList } = info;
      
      // Update file list (limit to 1 file)
      setFileList(newFileList.slice(-1));

      if (file.status === "done") {
        message.success(`${file.name} yinjijwe neza!`);
      } else if (file.status === "error") {
        message.error(`${file.name} ntiyashoboje kwinjizwa.`);
        setFileList([]);
        setFormData((prev) => ({
          ...prev,
          fileId: null,
        }));
      }
    },
    onRemove: () => {
      setFileList([]);
      setFormData((prev) => ({
        ...prev,
        fileId: null,
      }));
      message.info("Dosiye yakuwemo");
    },
  };

  return (
    <form>
      {/* Payment Type Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">
          Hitamo uburyo bwo kwishyura
        </label>
        <select
          value={formData.type}
          onChange={handlePaymentTypeChange}
          className="w-full focus:outline-none px-3 py-2 border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
        >
          <option value="">-- Hitamo --</option>
          <option value="pay_momo">Kwishyura kuri Mobile Money</option>
          <option value="pay_file">Injiza inyemezabuguzi</option>
        </select>
      </div>

      {/* Mobile Money Payment */}
      {formData.type === "pay_momo" && (
        <div className="mb-6">
          <h3 className="text-md font-semibold mb-3 border-b pb-2">
            Ishyura Na Mobile Money
          </h3>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Nimero ya telefone
            </label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="078..."
              maxLength={10}
              className="w-full focus:outline-none px-3 py-2 border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
            />
            <p className="text-xs text-gray-400 mt-1">
              Injiza nimero ya telefone uzakoresheje kwishyura
            </p>
          </div>
        </div>
      )}

      {/* File Upload Payment */}
      {formData.type === "pay_file" && (
        <div className="mb-6">
          <h3 className="text-md font-semibold mb-3 border-b pb-2">
            Injiza inyemezabuguzi
          </h3>

          <div className="mb-4">
            <Dragger {...uploadProps} disabled={uploading}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: "#1890ff" }} />
              </p>
              <p className="pb-4 text-xl text-white">
                {uploading ? "Iri gushyirwa..." : "Kanda hano cyangwa ukurure dosiye"}
              </p>
              <p className="text-sm text-white">
                Wemerewe gushyiramo ifoto (JPG, PNG) cyangwa PDF. Max: 5MB
              </p>
            </Dragger>

            {fileList.length > 0 && fileList[0].status === "done" && (
              <div className="mt-2 text-sm text-green-400">
                ✓ Dosiye yinjijwe neza: {fileList[0].name}
              </div>
            )}

            {uploading && (
              <div className="mt-2 text-sm text-blue-400">
                ⏳ Iri gushyirwamo, tegereza...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Instructions */}
      {formData.type && (
        <div className="mt-4 p-4 bg-blue-900/30 rounded-lg border border-blue-700">
          <p className="text-sm text-gray-300">
            {formData.type === "pay_momo" ? (
              <>
                <strong>Icyitonderwa:</strong> Urahabwa kwishyura ukoresheje
                nimero yanditse hejuru nyuma yo kohereza iyi fomu
              <br/>  <span className="text-center font-bold text-yellow-500" >MOMO KWEMEZA/POP: *182*7*1#</span>
              </>
            ) : (
              <>
                <strong>Icyitonderwa:</strong> Wemerewe gushyiramo
                gusa inyemezabuguzi y'ukuri. Dosiye itemewe ishobora gutuma
                igikorwa girasubira inyuma.
              </>
            )}
          </p>
        </div>
      )}
    </form>
  );
}

export default PaymentInformation;